"use client";

import NumberFlow from "@number-flow/react";
import localFont from "next/font/local";
import { useEffect } from "react";

import { useCountdown } from "@/hooks/use-countdown";
import { cn } from "@/utils/lib";

const digitalFont = localFont({ src: "../../fonts/digital-7.ttf" });

interface TimerUnitProps {
  value: string;
  name: string;
  className?: string;
  headingClassName?: string;
  timestampClassName?: string;
}

const TimerUnit = ({
  value,
  name,
  className,
  headingClassName,
  timestampClassName,
}: TimerUnitProps) => (
  <div
    className={cn(
      `
        relative flex animate-fade flex-col items-center text-center
        animate-once
      `,
      className,
    )}
  >
    <div
      className={cn(
        `
          mb-0 h-[74%] rounded-lg bg-red-900/20 p-2 text-center font-mono
          leading-none text-red-500 uppercase
        `,
        digitalFont.className,
        timestampClassName,
      )}
    >
      <NumberFlow
        value={Number.parseInt(value) < 0 ? 0 : Number.parseInt(value)}
        format={{ minimumIntegerDigits: 2 }}
      />
    </div>

    <h2
      className={cn(
        `
          absolute bottom-0 h-1/5 font-mono text-xs text-red-600
          sm:text-sm
        `,
        headingClassName,
      )}
    >
      {name}
    </h2>
  </div>
);

interface CountdownTimerProps {
  date: Date;
  onReset: () => void;
  timerClassName?: string;
  headingClassName?: string;
  timestampClassName?: string;
  shortLabels?: boolean;
  containerClassName?: string;
}

const CountdownTimer = ({
  date,
  onReset,
  timerClassName,
  headingClassName,
  timestampClassName,
  containerClassName,
  shortLabels = false,
}: CountdownTimerProps) => {
  const [days, hours, minutes, seconds] = useCountdown(date.getTime());

  useEffect(() => {
    // reset the timer if time goes <= 0
    if ([days, hours, minutes, seconds].every((n) => Number.parseInt(n) <= 0)) {
      onReset();
    }
  }, [seconds]);

  const units = [
    { value: days, name: shortLabels ? "d" : "days" },
    { value: hours, name: shortLabels ? "h" : "hours" },
    { value: minutes, name: shortLabels ? "m" : "minutes" },
    { value: seconds, name: shortLabels ? "s" : "seconds" },
  ];

  return (
    <div className={cn("relative flex items-center gap-4", containerClassName)}>
      <div className="absolute flex gap-4 opacity-15">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className={cn(
              "relative flex flex-col items-center",
              timerClassName,
            )}
          >
            <div
              className={cn(
                `
                  h-[74%] rounded-lg p-2 text-center font-mono leading-none
                  uppercase
                `,
                digitalFont.className,
                timestampClassName,
                `
                  text-muted
                  dark:text-red-500
                `,
              )}
            >
              88
            </div>
          </div>
        ))}
      </div>
      {units.map((unit, index) => (
        <TimerUnit
          key={index}
          value={unit.value}
          name={unit.name}
          className={timerClassName}
          headingClassName={headingClassName}
          timestampClassName={timestampClassName}
        />
      ))}
    </div>
  );
};

export default CountdownTimer;
